import { getProfile, user, isLoggingOut, profileStore } from '$lib/user';
import { setSupabaseAuthJWT } from '$lib/supabase';
import { createParaViemClient, createParaAccount } from '@getpara/viem-v2-integration';
import { selectOrInsertProfile } from '$lib/user/para/utils';
import { goto } from '$app/navigation';
import { showParaModal, showLoginModal, closeParaModal } from './store';
import { createWalletClient, http, publicActions } from 'viem';
// , type Chain
import { optimism, base, optimismSepolia } from 'viem/chains';
import { walletBalance } from '../wallet.store';
import { get } from 'svelte/store';
import { profileSchema } from '$lib/user/profile/profile.store';

// Helper function to check and sync Para client authentication state
export const checkAndSyncParaAuth = async () => {
	const { getParaClient } = await import('$lib/user/para/client');

	try {
		const paraClient = await getParaClient();

		// Add defensive check for client initialization
		if (!paraClient) {
			// console.warn('Para client not initialized');
			return false;
		}

		// Add additional validation to prevent privateKey errors
		try {
			const isLoggedIn = await paraClient.isFullyLoggedIn();
			const currentUser = get(user);

			// If Para thinks user is logged in but app user is null/undefined, clear Para session
			if (isLoggedIn && !currentUser) {
				await paraClient.logout();
				return false;
			}

			// If Para thinks user is not logged in but app has user, this is also out of sync
			if (!isLoggedIn && currentUser) {
				// Don't auto-login here, let the calling function handle it
				return false;
			}

			// Additional validation: try to export session to ensure wallet is properly initialized
			if (isLoggedIn) {
				try {
					const session = paraClient.exportSession();
					if (!session || session.length === 0) {
						// Session is empty or invalid, clear it
						await paraClient.logout();
						return false;
					}

					// --- NEW: Always update profileStore.walletAddress from session ---
					let sessionData;
					try {
						sessionData = JSON.parse(session);
					} catch (error) {
						try {
							sessionData = JSON.parse(atob(session));
						} catch (base64Error) {
							try {
								const parts = session.split('.');
								if (parts.length >= 2) {
									sessionData = JSON.parse(atob(parts[1]));
								} else {
									throw new Error('Invalid session format');
								}
							} catch (jwtError) {
								console.error('Failed to parse Para session - invalid format');
								throw new Error('Failed to parse Para session');
							}
						}
					}
					const walletIds = sessionData.currentWalletIds?.EVM || [];
					const walletId = walletIds[0];
					const walletData = sessionData.wallets?.[walletId];
					const walletAddress = walletData?.address || undefined;
					if (walletAddress) {
						profileStore.update((profile) => ({ ...profile, walletAddress }));
					}
					// --- END NEW ---
				} catch (sessionError) {
					console.warn('Session export failed during auth check:', sessionError);
					// Session export failed, likely due to privateKey issues
					await paraClient.logout();
					return false;
				}
			}

			return isLoggedIn;
		} catch (authError: unknown) {
			// Handle specific privateKey errors
			if (
				authError instanceof Error &&
				(authError.message?.includes('privateKey') || authError.message?.includes('undefined'))
			) {
				console.warn('Wallet session corrupted, clearing session');
				try {
					await paraClient.logout();
				} catch (logoutError) {
					// Silent error handling for logout
				}
				// Clear stored session from localStorage
				if (typeof window !== 'undefined') {
					localStorage.removeItem('para_session');
				}
				return false;
			}
			throw authError;
		}
	} catch (error) {
		console.warn('Error checking Para auth state:', error);
		// If there's an error checking auth state, assume not authenticated
		// This commonly happens during login/logout race conditions
		try {
			const paraClient = await getParaClient();
			if (paraClient) {
				await paraClient.logout();
			}
		} catch (logoutError) {
			// Silent error handling for logout
		}
		// Clear stored session from localStorage
		if (typeof window !== 'undefined') {
			localStorage.removeItem('para_session');
		}
		return false;
	}
};

export const paraLogout = async () => {
	const { getParaClient } = await import('$lib/user/para/client');
	const { page } = await import('$app/stores');
	const { get } = await import('svelte/store');

	// Set logout flag to prevent auth redirects
	isLoggingOut.set(true);

	// Get CSRF token before clearing user state
	const pageData = get(page);
	const csrfToken = pageData.data.csrfToken || '';

	// Clear Para client session
	const paraClient = await getParaClient();
	try {
		await paraClient.logout();
	} catch (error) {
		console.error('Para client logout error:', error);
	}

	// NEW: Clear stored session from localStorage
	if (typeof window !== 'undefined') {
		localStorage.removeItem('para_session');
	}

	const exportedSession = paraClient.exportSession();

	// Make the logout API call with CSRF token
	try {
		const response = await fetch('/api/paraauth', {
			method: 'DELETE',
			headers: {
				'Content-Type': 'application/json',
				'x-csrf-token': csrfToken
			},
			body: JSON.stringify({
				paraSession: exportedSession
			})
		});

		if (!response.ok) {
			const errorText = await response.text();
			console.error(
				'Para logout failed:',
				response.statusText,
				'Status:',
				response.status,
				'Error:',
				errorText
			);
		}
	} catch (error) {
		console.error('Logout API call error:', error);
	}

	// Navigate to home first to avoid any auth route flashing
	goto('/', { replaceState: true });

	// Clear user state after navigation starts
	user.set(undefined);
	profileStore.set(profileSchema);
	setSupabaseAuthJWT(null);
	closeParaModal();

	// Reset logout flag after a short delay to ensure navigation completes
	setTimeout(() => {
		isLoggingOut.set(false);
	}, 500);
};

export const paraLogin = async () => {
	const { getParaClient } = await import('$lib/user/para/client');
	const { page } = await import('$app/stores');
	const { get } = await import('svelte/store');

	// Ensure Para client is logged out before showing login modal
	try {
		const paraClient = await getParaClient();

		if (paraClient) {
			const isLoggedIn = await paraClient.isFullyLoggedIn();

			if (isLoggedIn) {
				await paraClient.logout();
			}
		}
	} catch (error) {
		console.error('Para client logout check error:', error);
	}

	showLoginModal();

	await new Promise<void>((res) => {
		showParaModal.subscribe((value) => {
			if (value === false) {
				res();
			}
		});
	});

	try {
		const paraClient = await getParaClient();

		if (!paraClient) {
			// console.error('Para client not available after login');
			return false;
		}

		// Ensure Para client is properly initialized
		await new Promise((resolve) => setTimeout(resolve, 500));

		const isLoggedIn = await paraClient.isFullyLoggedIn();

		if (!isLoggedIn) {
			// console.error('Para client not logged in after modal');
			return false;
		}

		// Add a small delay to ensure Para client is fully ready
		await new Promise((resolve) => setTimeout(resolve, 200));

		// Additional check to ensure Para session is ready
		try {
			const session = paraClient.exportSession();
		} catch (error) {
			console.warn('Error checking Para session:', error);
		}

		// Add a delay to ensure Para session is fully established
		await new Promise((resolve) => setTimeout(resolve, 1000));

		// Get wallet address from Para session instead of making API call
		const paraSession = paraClient.exportSession();

		// Try to parse the session safely
		let sessionData;
		try {
			// First try to parse as JSON directly
			sessionData = JSON.parse(paraSession);
		} catch (error) {
			try {
				// If that fails, try parsing as base64-encoded JSON
				sessionData = JSON.parse(atob(paraSession));
			} catch (base64Error) {
				try {
					// If that fails, try parsing as JWT
					const parts = paraSession.split('.');
					if (parts.length >= 2) {
						sessionData = JSON.parse(atob(parts[1]));
					} else {
						throw new Error('Invalid session format');
					}
				} catch (jwtError) {
					console.error('Failed to parse Para session - invalid format');
					throw new Error('Failed to parse Para session');
				}
			}
		}

		// Extract wallet address from session
		const walletIds = sessionData.currentWalletIds?.EVM || [];
		if (!walletIds || walletIds.length === 0) {
			throw new Error('No wallet found in Para session');
		}

		const walletId = walletIds[0];
		const walletData = sessionData.wallets?.[walletId];
		if (!walletData?.address) {
			throw new Error('No wallet address found in Para session');
		}

		// NEW: Defensive check - validate wallet object exists and is properly configured
		let walletOk = false;
		try {
			// Check if wallet has the necessary properties for signing
			if (walletData && walletData.address) {
				// Check if the wallet is properly initialized by trying to export the session
				// This validates the wallet is ready without accessing private keys
				try {
					const testSession = paraClient.exportSession();
					if (testSession && testSession.length > 0) {
						walletOk = true;
					}
				} catch (sessionError) {
					// Session export failed, wallet not ready
					console.warn('Session export failed during wallet validation:', sessionError);
				}
			}
		} catch (error) {
			console.warn('Error during wallet validation:', error);
		}

		if (!walletOk) {
			await paraClient.logout();
			throw new Error('Wallet session could not be established. Please try again.');
		}

		const walletAddress: string = walletData.address;
		const email = paraClient.getEmail();

		if (!walletAddress) {
			throw new Error('No wallet address found');
		}

		if (!email) {
			throw new Error('No email found');
		}

		// Update the Svelte profile store with the Para wallet address
		profileStore.update((profile) => ({
			...profile,
			walletAddress
		}));

		const {
			id: userId,
			member,
			wallet: userWallet
		} = await selectOrInsertProfile(email, walletAddress);

		const exportedSession = await paraClient.exportSession();

		// NEW: Store session in localStorage with 7-day expiry
		if (typeof window !== 'undefined') {
			const sessionData = {
				session: exportedSession,
				expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000 // 7 days
			};
			localStorage.setItem('para_session', JSON.stringify(sessionData));
		}

		const pageData = get(page);

		const response = await fetch('/api/paraauth', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'x-csrf-token': pageData.data.csrfToken || ''
			},
			body: JSON.stringify({
				paraSession: exportedSession
			})
		});

		const { jwt, session: supabaseSession } = await response.json();
		setSupabaseAuthJWT(jwt, supabaseSession);

		// TODO: insert wallet into the user's profile record here - if data but not wallet, insert wallet into profile record
		if (!userWallet) {
			const { safeUpdateWallet } = await import('$lib/user/profile/profile.api');
			const success = await safeUpdateWallet(userId, walletAddress);

			if (!success) {
				console.error('Failed to update wallet in profile');
				throw new Error('Failed to update wallet in profile');
			}
		}

		user.set({
			walletAddress,
			email,
			id: userId,
			member
		});

		// // Ensure the user store has been set before calling getProfile
		// console.log('User store set, current user:', { walletAddress, email, id: userId, member });
		// console.log('User store after set:', get(user));

		// Longer delay to ensure Para client is fully initialized and JWT is properly applied
		await new Promise((resolve) => setTimeout(resolve, 500));

		await getProfile();

		return true;
	} catch (err) {
		console.error('Para login failed:', err);

		// If Para client is having issues, try to clean up and return false
		try {
			const paraClient = await getParaClient();
			if (paraClient) {
				await paraClient.logout();
			}
		} catch (cleanupError) {
			console.warn('Error during Para cleanup:', cleanupError);
		}

		return false;
	}
};

// Create the viem client for interacting with the blockchain
// const viemClient = createWalletClient({
// 	chain: optimism,
// 	transport: http('https://mainnet.optimism.io')
// }).extend(publicActions);

// Create viem clients for both chains
const optimismClient = createWalletClient({
	chain: optimism,
	transport: http('https://mainnet.optimism.io')
}).extend(publicActions);

const baseClient = createWalletClient({
	chain: base,
	transport: http('https://mainnet.base.org')
}).extend(publicActions);

// Create a Sepolia client
const sepoliaClient = createWalletClient({
	chain: optimismSepolia,
	transport: http('https://sepolia.optimism.io')
}).extend(publicActions);

// const sepolia = {
// 	id: 11155111, // Sepolia's chain ID
// 	name: 'Sepolia',
// 	nativeCurrency: {
// 		name: 'Sepolia Ether',
// 		symbol: 'ETH',
// 		decimals: 18
// 	},
// 	rpcUrls: {
// 		default: {
// 			http: ['https://sepolia.infura.io/v3/YOUR_INFURA_PROJECT_ID'] // Replace with your RPC URL
// 		}
// 	}
// };

// Example function to get balance on Sepolia
export const getSepoliaBalance = async (userAddress: string) => {
	try {
		const rawSepoliaEthBalance = await sepoliaClient.getBalance({
			address: userAddress as `0x${string}`
		});
		const sepoliaEthBalance = (
			Math.floor(parseFloat(rawSepoliaEthBalance.toString()) / Math.pow(10, 13)) / Math.pow(10, 5)
		).toString();

		// console.log('Sepolia ETH Balance:', sepoliaEthBalance);
		return sepoliaEthBalance;
	} catch (error) {
		console.error('Error fetching Sepolia balance:', error as Error);
		return 'Error';
	}
};

// get iUSD balance
async function getSepoliaIUSDBalance(userAddress: string): Promise<string> {
	try {
		const iUSDAddress = tokenAddresses.optimismSepolia.iUSD;
		const balanceRaw = await sepoliaClient.call({
			to: iUSDAddress,
			data: `0x70a08231000000000000000000000000${userAddress.slice(2).toLowerCase()}`
		});
		return (parseInt((balanceRaw.data ?? '0x0').slice(2), 16) / Math.pow(10, 18)).toString();
	} catch (error) {
		console.error('Error fetching iUSD balance:', error);
		return '0';
	}
}

export const getWalletBalances = async (userAddress: string) => {
	try {
		// console.log('Fetching balances for account:', userAddress);

		// Make all blockchain calls in parallel for better performance
		const [
			rawOptimismEthBalance,
			optimismUsdcBalanceRaw,
			bloomBalanceRaw,
			rawBaseEthBalance,
			baseUsdcBalanceRaw
		] = await Promise.all([
			optimismClient.getBalance({
				address: userAddress as `0x${string}`
			}),
			optimismClient.call({
				to: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
				data: `0x70a08231000000000000000000000000${userAddress.slice(2).toLowerCase()}`
			}),
			optimismClient.call({
				to: '0x4Ab1BFC688992e7c6C344d0a31C5CBd6FbD86f33',
				data: `0x70a08231000000000000000000000000${userAddress.slice(2).toLowerCase()}`
			}),
			baseClient.getBalance({
				address: userAddress as `0x${string}`
			}),
			baseClient.call({
				to: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
				data: `0x70a08231000000000000000000000000${(userAddress as `0x${string}`).slice(2).toLowerCase()}`
			})
		]);

		// Process results
		const optimismEthBalance = (
			Math.floor(parseFloat(rawOptimismEthBalance.toString()) / Math.pow(10, 13)) / Math.pow(10, 5)
		).toString();

		const optimismUsdcBalance = (
			parseInt(optimismUsdcBalanceRaw.toString().slice(2), 16) / Math.pow(10, 6)
		).toString();

		const bloomBalance = (
			parseInt((bloomBalanceRaw.data ?? '0x0').slice(2), 16) / Math.pow(10, 18)
		).toString();

		const baseEthBalance = (
			Math.floor(parseFloat(rawBaseEthBalance.toString()) / Math.pow(10, 13)) / Math.pow(10, 5)
		).toString();

		const rawValue = BigInt(baseUsdcBalanceRaw.data ?? '0x0');
		const baseUsdcBalance = (Number(rawValue) / 1_000_000).toString();

		// Update the wallet balance store
		walletBalance.set({
			optimism: {
				ethBalance: optimismEthBalance,
				usdcBalance: optimismUsdcBalance,
				bloomBalance: bloomBalance
			},
			base: {
				ethBalance: baseEthBalance,
				usdcBalance: baseUsdcBalance
			}
		});
		// console.log('Store updated with new balances');
	} catch (error) {
		console.error('Error fetching wallet balances:', error as Error);
		walletBalance.set({
			optimism: {
				ethBalance: 'Error',
				usdcBalance: 'Error',
				bloomBalance: 'Error'
			},
			base: {
				ethBalance: 'Error',
				usdcBalance: 'Error'
			}
		});
	}
};

//SEND USING VIEM SETUP INSTRUCTIONS

// Converts Ether value to wei
export function parseEther(value: string): bigint {
	return BigInt(Math.floor(parseFloat(value) * 1e18));
}

// Token addresses with proper typing
const tokenAddresses = {
	optimism: {
		USDC: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
		FLO: '0x4Ab1BFC688992e7c6C344d0a31C5CBd6FbD86f33'
	} as const,
	base: {
		USDC: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913'
	} as const,
	optimismSepolia: {
		iUSD: '0x065775C7aB4E60ad1776A30DCfB15325d231Ce4F'
	} as const
} as const;

// Add this before sendTransaction function
const displayToBlockchainSymbol = {
	'ETH on Base': 'ETH',
	'ETH on Optimism': 'ETH',
	'FLO Token on Optimism': 'FLO',
	'USDC on Base': 'USDC',
	'USDC on Optimism': 'USDC',
	iUSD: 'iUSD',
	FLO: 'FLO'
} as const;

// Function to send a transaction
export const sendTransaction = async (
	recipient: string,
	amount: string,
	selectedChain: typeof optimism | typeof base | typeof optimismSepolia,
	displaySymbol: string
): Promise<string> => {
	const { getParaClient } = await import('$lib/user/para/client');
	// Check authentication first
	const paraClient = await getParaClient();
	const isLoggedIn = await paraClient.isFullyLoggedIn();
	if (!isLoggedIn) {
		const loginSuccess = await paraLogin();
		if (!loginSuccess) {
			throw new Error('Failed to reconnect to Para. Please try again.');
		}
	}

	const tokenSymbol =
		displayToBlockchainSymbol[displaySymbol as keyof typeof displayToBlockchainSymbol];

	if (!recipient || !amount) {
		throw new Error('Recipient address and amount are required.');
	}
	if (!/^0x[a-fA-F0-9]{40}$/.test(recipient)) {
		throw new Error('Invalid recipient address.');
	}
	if (!tokenSymbol) {
		throw new Error('Token symbol is required');
	}

	const paraAccount = await createParaAccount(paraClient);

	// Check token balance before proceeding
	await getWalletBalances(paraAccount.address);
	const walletBalances = get(walletBalance);

	// Get current balance based on chain and token
	let currentBalance = '0';
	if (selectedChain.id === optimism.id) {
		if (displaySymbol === 'FLO') {
			currentBalance = walletBalances.optimism.bloomBalance;
		} else if (displaySymbol === 'USDC on Optimism') {
			currentBalance = walletBalances.optimism.usdcBalance;
		} else if (displaySymbol === 'ETH on Optimism') {
			currentBalance = walletBalances.optimism.ethBalance;
		}
	} else if (selectedChain.id === base.id) {
		if (displaySymbol === 'USDC on Base') {
			currentBalance = walletBalances.base.usdcBalance;
		} else if (displaySymbol === 'ETH on Base') {
			currentBalance = walletBalances.base.ethBalance;
		}
	} else if (selectedChain.id === optimismSepolia.id) {
		if (displaySymbol === 'iUSD') {
			currentBalance = await getSepoliaIUSDBalance(paraAccount.address);
		}
	}

	// Check token balance with clear error message
	if (Number(currentBalance) < Number(amount)) {
		throw new Error(
			`Insufficient ${displaySymbol} balance. You have ${currentBalance} ${displaySymbol} but the transaction requires ${amount} ${displaySymbol}.`
		);
	}

	const paraViemSigner = createParaViemClient(paraClient, {
		account: paraAccount,
		chain: selectedChain,
		transport: http(
			selectedChain.id === optimism.id
				? 'https://mainnet.optimism.io'
				: selectedChain.id === optimismSepolia.id
					? 'https://sepolia.optimism.io'
					: 'https://mainnet.base.org'
		)
	});

	// Prepare the transaction object
	try {
		if (tokenSymbol === 'ETH') {
			// Native ETH transfer
			const transaction = {
				account: paraAccount,
				chain: selectedChain,
				to: recipient as `0x${string}`,
				value: parseEther(amount),
				gas: BigInt(21000)
			};

			const txHash = await paraViemSigner.sendTransaction(transaction);

			await getWalletBalances(paraAccount.address);

			return txHash;
		} else {
			// ERC20 token transfer
			let tokenAddress: string | undefined;

			if (selectedChain.id === optimism.id) {
				tokenAddress = tokenAddresses.optimism[tokenSymbol as keyof typeof tokenAddresses.optimism];
				// console.log('Debug - Optimism token address:', tokenAddress);
			} else if (selectedChain.id === base.id) {
				tokenAddress = tokenAddresses.base[tokenSymbol as keyof typeof tokenAddresses.base];
				// console.log('Debug - Base token address:', tokenAddress);
			} else if (selectedChain.id === optimismSepolia.id) {
				tokenAddress =
					tokenAddresses.optimismSepolia[
						tokenSymbol as keyof typeof tokenAddresses.optimismSepolia
					];
			}

			if (!tokenAddress) {
				throw new Error(`Token ${tokenSymbol} not supported on ${selectedChain.name}`);
			}

			// Create ERC20 transfer data
			const decimals = tokenSymbol === 'USDC' ? 6 : 18;
			const value = BigInt(Math.floor(parseFloat(amount) * Math.pow(10, decimals)));
			const data = `0xa9059cbb${recipient.slice(2).padStart(64, '0')}${value.toString(16).padStart(64, '0')}`;

			const transaction = {
				account: paraAccount,
				chain: selectedChain,
				to: tokenAddress as `0x${string}`,
				data: data as `0x${string}`,
				gas: BigInt(100000)
			};

			// console.log('Debug - Transaction:', transaction);
			const txHash = await paraViemSigner.sendTransaction(transaction);

			// Add balance refresh after successful transaction
			await getWalletBalances(paraAccount.address);

			return txHash;
		}
	} catch (error) {
		console.error('Error signing or sending transaction:', error);
		if (error instanceof Error) {
			const errorMessage = error.message;

			// Handle specific error types
			if (errorMessage?.includes('WebSocket')) {
				throw new Error('Connection to wallet lost. Please refresh and try again.');
			} else if (
				errorMessage?.includes('insufficient funds for gas * price + value') ||
				(errorMessage?.includes('insufficient funds') && errorMessage?.includes('gas'))
			) {
				throw new Error(
					'Insufficient ETH for gas fees. You need ETH in your wallet to pay for transaction fees, even when sending tokens.'
				);
			} else if (errorMessage?.includes('insufficient funds') && !errorMessage?.includes('gas')) {
				throw new Error('Insufficient token balance for this transaction.');
			} else if (errorMessage?.includes('gas')) {
				throw new Error(
					'Gas fee error. Please ensure you have some ETH in your wallet for transaction fees.'
				);
			} else {
				throw new Error(errorMessage);
			}
		}
		throw new Error('Transaction failed');
	}
};
